/**
 * RoomDiscoveryPicker — building + room-number + bed + bath, the four
 * inputs that fully describe a dorm room when no manifest exists.
 *
 * Two dropdowns + two toggles, in this order:
 *   1. Building       — searchable; populated from useOrgBuildings().
 *   2. Room number    — generated 1xx..Nxx where N is the building's
 *                       floor_count. 24 doors per floor.
 *   3. Bed count      — single (1) or double (2).
 *   4. Bathroom count — single (1) or double (2).
 *
 * If the cleaner picks a room number that already exists for the
 * building, bed/bath pre-fill from the existing row (editable in case
 * the first cleaner mis-set them). New numbers default to single/single.
 *
 * Submit calls useClaimRoom → claim-room Edge Function → upserts the
 * room (creates the floor on the fly if needed). On success the parent
 * receives the resolved room_id via onRoomClaimed and proceeds to its
 * next step (assign a task, transition status, etc.).
 *
 * Pre-go-live (2026-05-18) for ABC's first contract — the school has
 * not released floor plans or room manifests, so cleaners build the
 * inventory at first sighting.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type FormEvent } from 'react'
import { useOrgBuildings, useBuildingRooms } from '@/hooks/useOrgPickers'
import { useClaimRoom }                       from '@/hooks/useClaimRoom'
import { isAppError }                         from '@/lib/errors/AppError'
import { generateRoomNumbers, pickerFloorCount, MAX_FLOOR_CEILING } from './roomNumberList'
import { isSpeechRecognitionSupported, startListening } from '@/lib/voice/speechRecognition'
import { parseRoomNumber }                    from '@/lib/voice/parseRoomNumber'

export interface ClaimedRoomSummary {
  roomId:        string
  roomNumber:    string
  buildingId:    string
  buildingName:  string
  bedCount:      number
  bathroomCount: number
  wasCreated:    boolean
}

export interface RoomDiscoveryPickerProps {
  /** Called with the resolved room after a successful claim. */
  onRoomClaimed: (summary: ClaimedRoomSummary) => void
  /** Disable interaction while the parent is doing further work. */
  disabled?: boolean
}

type BedOrBath = 1 | 2

export function RoomDiscoveryPicker({ onRoomClaimed, disabled = false }: RoomDiscoveryPickerProps): ReactNode {
  const buildings = useOrgBuildings()
  const claim     = useClaimRoom()

  const [buildingId,     setBuildingId]     = useState<string>('')
  const [roomNumber,     setRoomNumber]     = useState<string>('')
  const [bedCount,       setBedCount]       = useState<BedOrBath>(1)
  const [bathroomCount,  setBathroomCount]  = useState<BedOrBath>(1)
  const [manualMode,     setManualMode]     = useState<boolean>(false)
  const [manualInputErr, setManualInputErr] = useState<string | null>(null)

  const buildingRooms = useBuildingRooms(buildingId || null)

  const [voiceListening, setVoiceListening] = useState(false)
  const [voiceMessage,   setVoiceMessage]   = useState<{ kind: 'info' | 'error'; text: string } | null>(null)
  const stopListenRef = useRef<(() => void) | null>(null)
  const voiceSupported = useMemo(() => isSpeechRecognitionSupported(), [])

  useEffect(() => () => {
    // Component unmount: make sure no recognition session is left
    // listening. Mic indicator should always release.
    if (stopListenRef.current) {
      stopListenRef.current()
      stopListenRef.current = null
    }
  }, [])

  const selectedBuilding = useMemo(
    () => (buildings.data ?? []).find((b) => b.id === buildingId) ?? null,
    [buildings.data, buildingId],
  )

  const roomNumberOptions = useMemo(
    () => generateRoomNumbers(pickerFloorCount(selectedBuilding?.floor_count ?? null)),
    [selectedBuilding?.floor_count],
  )

  const existingRoom = useMemo(
    () => (buildingRooms.data ?? []).find((r) => r.room_number === roomNumber) ?? null,
    [buildingRooms.data, roomNumber],
  )

  // Pre-fill bed/bath when the cleaner picks an existing room. When
  // they toggle back to a brand-new number, snap to the safe 1/1
  // defaults so the previous existing-room values don't bleed into a
  // new claim (audit F5 — first-cleaner-on-this-door must not inherit
  // an unrelated room's bed/bath shape).
  //
  // Adjusted during render rather than in an effect. The sync key is the
  // room number plus the identifying + prefilled fields of the matched
  // room, so the prefill still runs on every case that matters: picking a
  // number, switching rooms, and `useBuildingRooms` resolving (which flips
  // existingRoom from null to a row).
  //
  // One deliberate difference from the effect this replaces: `existingRoom`
  // comes from `.find()`, so a background refetch produced a fresh object
  // every time and re-ran the prefill, silently discarding any manual
  // bed/bath edit the cleaner had made. Keying on the values instead of the
  // object identity means an unchanged refetch no longer clobbers their
  // input. A genuine server-side change to bed_count/bathroom_count still
  // re-prefills, and the F5 reset path is untouched.
  const syncKey = existingRoom
    ? `${roomNumber}|${existingRoom.id}|${existingRoom.bed_count ?? ''}|${existingRoom.bathroom_count ?? ''}`
    : `${roomNumber}|`
  const [prevSyncKey, setPrevSyncKey] = useState<string | null>(null)
  if (syncKey !== prevSyncKey) {
    setPrevSyncKey(syncKey)
    if (existingRoom) {
      setBedCount(toBedOrBath(existingRoom.bed_count))
      setBathroomCount(toBedOrBath(existingRoom.bathroom_count))
    } else if (roomNumber) {
      setBedCount(1)
      setBathroomCount(1)
    }
  }

  function handleBuildingChange(next: string): void {
    setBuildingId(next)
    setRoomNumber('')
    setBedCount(1)
    setBathroomCount(1)
    setVoiceMessage(null)
    setManualInputErr(null)
  }

  /**
   * Validate a manually-typed room number. The dropdown enforces the
   * (floor ≤ pickerFloorCount, offset ≤ 24) convention; manual mode
   * loosens to (1 ≤ floor ≤ MAX_FLOOR_CEILING, 1 ≤ offset ≤ 99) so a
   * cleaner can type a real room from an unusual building shape
   * (rooms 125–199 within a floor, floor 6 in a tall building, etc).
   */
  function validateManualRoom(value: string): string | null {
    if (!/^\d{3,4}$/.test(value)) return 'Type a 3- or 4-digit room number.'
    const n = Number.parseInt(value, 10)
    const floor  = Math.floor(n / 100)
    const offset = n % 100
    if (floor < 1 || floor > MAX_FLOOR_CEILING) return `Floor must be between 1 and ${MAX_FLOOR_CEILING}.`
    if (offset < 1 || offset > 99)              return 'Room number must end in 01–99.'
    return null
  }

  function handleManualToggle(next: boolean): void {
    setManualMode(next)
    setRoomNumber('')
    setBedCount(1)
    setBathroomCount(1)
    setManualInputErr(null)
    setVoiceMessage(null)
  }

  function handleManualChange(value: string): void {
    const trimmed = value.replace(/\D/g, '').slice(0, 4)
    setRoomNumber(trimmed)
    setManualInputErr(trimmed === '' ? null : validateManualRoom(trimmed))
  }

  const handleVoiceClick = useCallback((): void => {
    if (voiceListening) {
      stopListenRef.current?.()
      stopListenRef.current = null
      setVoiceListening(false)
      return
    }
    if (!buildingId) {
      setVoiceMessage({ kind: 'error', text: 'Pick a building first.' })
      return
    }
    setVoiceMessage({ kind: 'info', text: 'Listening — say the room number.' })
    const stop = startListening({
      onResult: (transcript) => {
        const { roomNumber: parsed } = parseRoomNumber(transcript)
        if (!parsed) {
          // Audit F4: never echo the raw transcript — anything the
          // cleaner spoke before the digits (school name, building
          // landmarks, casual chatter) is NDA-restricted text we
          // refuse to render anywhere downstream of the mic.
          setVoiceMessage({
            kind: 'error',
            text: "Couldn't catch a room number — try again or pick from the list.",
          })
          return
        }
        if (manualMode) {
          // Manual mode: the cleaner is intentionally targeting a
          // room outside the dropdown convention. Skip the dropdown
          // range check; still run the manual validator so an
          // obviously-wrong number (floor 0, offset 00) is rejected.
          const err = validateManualRoom(parsed)
          if (err) {
            setVoiceMessage({ kind: 'error', text: err })
            return
          }
          setRoomNumber(parsed)
          setManualInputErr(null)
          setVoiceMessage({ kind: 'info', text: `Set room ${parsed}.` })
          return
        }
        if (!roomNumberOptions.includes(parsed)) {
          setVoiceMessage({
            kind: 'error',
            text: `Heard room ${parsed}, but it's outside this building's range. Pick a lower number.`,
          })
          return
        }
        setRoomNumber(parsed)
        setVoiceMessage({ kind: 'info', text: `Set room ${parsed}.` })
      },
      onError: (code) => {
        setVoiceMessage({ kind: 'error', text: voiceErrorMessage(code) })
      },
      onEnd: () => {
        setVoiceListening(false)
        stopListenRef.current = null
      },
    })
    if (!stop) {
      setVoiceListening(false)
      setVoiceMessage({ kind: 'error', text: 'Could not start the microphone.' })
      return
    }
    stopListenRef.current = stop
    setVoiceListening(true)
  }, [voiceListening, buildingId, roomNumberOptions, manualMode])

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!buildingId || !roomNumber) return
    try {
      const result = await claim.mutateAsync({
        buildingId,
        roomNumber,
        bedCount,
        bathroomCount,
      })
      onRoomClaimed({
        roomId:        result.room_id,
        roomNumber,
        buildingId,
        buildingName:  selectedBuilding?.name ?? '',
        bedCount:      result.bed_count       as 1 | 2,
        bathroomCount: result.bathroom_count  as 1 | 2,
        wasCreated:    result.was_created,
      })
    } catch {
      // AppError surfaces inline below via claim.error — no toast.
    }
  }

  const errMsg     = claim.error && isAppError(claim.error) ? claim.error.userMessage : null
  const isBusy     = disabled || claim.isPending
  const canSubmit  = !isBusy && !!buildingId && !!roomNumber && (!manualMode || manualInputErr === null)

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="room-discovery-picker"
      style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
      aria-busy={claim.isPending}
    >
      <Field label="Building">
        <select
          data-testid="rd-building"
          value={buildingId}
          onChange={(e) => handleBuildingChange(e.target.value)}
          required
          disabled={isBusy || buildings.isLoading}
          style={inputStyle}
        >
          <option value="">{buildings.isLoading ? 'Loading…' : 'Pick a building'}</option>
          {(buildings.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </Field>

      <Field label="Room number">
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
          {manualMode ? (
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{3,4}"
              data-testid="rd-room-number-manual"
              value={roomNumber}
              onChange={(e) => handleManualChange(e.target.value)}
              placeholder="e.g. 213"
              required
              disabled={isBusy || !buildingId}
              maxLength={4}
              style={{ ...inputStyle, flex: 1 }}
              aria-invalid={manualInputErr !== null}
              aria-describedby={manualInputErr ? 'rd-room-number-manual-err' : undefined}
            />
          ) : (
            <select
              data-testid="rd-room-number"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              required
              disabled={isBusy || !buildingId}
              style={{ ...inputStyle, flex: 1 }}
            >
              <option value="">
                {!buildingId ? 'Pick a building first' : 'Pick a room number'}
              </option>
              {roomNumberOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          )}
          {voiceSupported && (
            <button
              type="button"
              data-testid="rd-mic"
              onClick={handleVoiceClick}
              disabled={isBusy || !buildingId}
              aria-label={voiceListening ? 'Stop listening' : 'Speak the room number'}
              aria-pressed={voiceListening}
              title={voiceListening ? 'Stop listening' : 'Speak the room number'}
              style={{
                background:    voiceListening ? 'var(--brand-danger)' : 'var(--brand-neutral-100)',
                color:         voiceListening ? 'var(--brand-white)'  : 'var(--brand-neutral-700)',
                border:        '1px solid var(--brand-neutral-300)',
                borderRadius:  'var(--brand-radius-sm)',
                padding:       '0 0.7rem',
                fontSize:      '1rem',
                cursor:        isBusy || !buildingId ? 'default' : 'pointer',
                minWidth:      '2.5rem',
              }}
            >
              {voiceListening ? '■' : '🎤'}
            </button>
          )}
        </div>
        {manualInputErr && manualMode && (
          <p
            id="rd-room-number-manual-err"
            data-testid="rd-room-number-manual-err"
            role="alert"
            style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--brand-danger)' }}
          >
            {manualInputErr}
          </p>
        )}
        <button
          type="button"
          data-testid="rd-manual-toggle"
          onClick={() => handleManualToggle(!manualMode)}
          disabled={isBusy}
          style={{
            background:   'transparent',
            border:       'none',
            color:        'var(--brand-primary)',
            fontSize:     '0.8rem',
            padding:      '0.1rem 0',
            cursor:       'pointer',
            textAlign:    'left',
            textDecoration: 'underline',
            alignSelf:    'flex-start',
          }}
        >
          {manualMode ? '↩ Back to the list' : '📝 Type a room not in the list'}
        </button>
        {voiceMessage && (
          <p
            data-testid="rd-voice-message"
            role={voiceMessage.kind === 'error' ? 'alert' : 'status'}
            style={{
              margin:    '0.25rem 0 0',
              fontSize:  '0.8rem',
              color:     voiceMessage.kind === 'error' ? 'var(--brand-danger)' : 'var(--brand-neutral-600)',
            }}
          >
            {voiceMessage.text}
          </p>
        )}
      </Field>

      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Beds in this room</legend>
        <RadioPair
          name="rd-bed"
          value={bedCount}
          onChange={setBedCount}
          disabled={isBusy || !roomNumber}
          oneLabel="Single bed"
          twoLabel="Double bed"
        />
      </fieldset>

      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Bathrooms in this room</legend>
        <RadioPair
          name="rd-bath"
          value={bathroomCount}
          onChange={setBathroomCount}
          disabled={isBusy || !roomNumber}
          oneLabel="Single bath"
          twoLabel="Double bath"
        />
      </fieldset>

      {errMsg && (
        <p role="alert" data-testid="rd-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
          {errMsg}
        </p>
      )}

      <button
        type="submit"
        data-testid="rd-submit"
        disabled={!canSubmit}
        style={{
          background:    'var(--brand-primary)',
          color:         'var(--brand-white)',
          border:        'none',
          borderRadius:  'var(--brand-radius-md)',
          padding:       '0.6rem 1.2rem',
          fontSize:      '0.95rem',
          fontWeight:    600,
          cursor:        canSubmit ? 'pointer' : 'default',
          opacity:       canSubmit ? 1 : 0.6,
        }}
      >
        {claim.isPending ? 'Claiming…' : 'Claim this room'}
      </button>
    </form>
  )
}

function toBedOrBath(value: number): BedOrBath {
  return value === 2 ? 2 : 1
}

function voiceErrorMessage(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed': return 'Microphone permission denied. Allow it in your browser, then try again.'
    case 'no-speech':           return "Didn't hear anything — try again."
    case 'audio-capture':       return 'No microphone detected on this device.'
    case 'network':             return 'Network error during voice recognition. Try again or type the room number.'
    case 'aborted':             return 'Listening cancelled.'
    case 'NO_TRANSCRIPT':       return "Didn't catch that — try again."
    case 'START_FAILED':        return 'Could not start the microphone.'
    default:                    return `Voice error: ${code}.`
  }
}

interface RadioPairProps {
  name:     string
  value:    BedOrBath
  onChange: (next: BedOrBath) => void
  disabled: boolean
  oneLabel: string
  twoLabel: string
}

function RadioPair({ name, value, onChange, disabled, oneLabel, twoLabel }: RadioPairProps): ReactNode {
  return (
    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <input
          type="radio"
          name={name}
          data-testid={`${name}-1`}
          checked={value === 1}
          onChange={() => onChange(1)}
          disabled={disabled}
        />
        {oneLabel}
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <input
          type="radio"
          name={name}
          data-testid={`${name}-2`}
          checked={value === 2}
          onChange={() => onChange(2)}
          disabled={disabled}
        />
        {twoLabel}
      </label>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
      {label}
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  fontSize:     '0.95rem',
  padding:      '0.45rem 0.55rem',
  borderRadius: 'var(--brand-radius-sm)',
  border:       '1px solid var(--brand-neutral-300)',
  background:   'var(--brand-white)',
}

const fieldsetStyle: React.CSSProperties = {
  border:       '1px solid var(--brand-neutral-300)',
  borderRadius: 'var(--brand-radius-sm)',
  padding:      '0.5rem 0.75rem 0.65rem',
  margin:       0,
}

const legendStyle: React.CSSProperties = {
  padding:    '0 0.3rem',
  fontSize:   '0.8rem',
  color:      'var(--brand-neutral-700)',
}
